import { useEffect, useRef } from "react";
import type { LipSyncMorphTarget } from "./useLipSync";

// Liveness Group A — random eye blinks every 3-5s with ~10% chance of a
// double-blink (two blinks ~120ms apart). Always-on (including during
// speech) since blinking is involuntary and natural while talking. Writes
// only to eyeBlinkLeft / eyeBlinkRight, which useLipSync never touches —
// no race condition with the viseme path.

export interface UseEyeBlinkOptions {
  morphTarget: LipSyncMorphTarget | null;
  enabled?: boolean;
  minIntervalMs?: number;
  maxIntervalMs?: number;
  doubleBlinkChance?: number;
}

const BLINK_MORPHS = ["eyeBlinkLeft", "eyeBlinkRight"] as const;
const BLINK_DURATION_MS = 200;

export function useEyeBlink({
  morphTarget,
  enabled = true,
  minIntervalMs = 3000,
  maxIntervalMs = 5000,
  doubleBlinkChance = 0.10,
}: UseEyeBlinkOptions): void {
  const rafRef = useRef<number | null>(null);
  const blinkStartRef = useRef<number | null>(null);
  const nextBlinkAtRef = useRef<number>(0);
  const isDoubleBlinkRef = useRef<boolean>(false);
  const blinkPhaseRef = useRef<0 | 1>(0);

  useEffect(() => {
    if (!enabled || !morphTarget) return;

    const scheduleNext = (now: number) => {
      const interval = minIntervalMs + Math.random() * (maxIntervalMs - minIntervalMs);
      nextBlinkAtRef.current = now + interval;
      isDoubleBlinkRef.current = Math.random() < doubleBlinkChance;
      blinkPhaseRef.current = 0;
    };

    scheduleNext(performance.now());

    const tick = () => {
      const now = performance.now();
      let weight = 0;

      if (blinkStartRef.current !== null) {
        const elapsed = now - blinkStartRef.current;
        if (elapsed < 80) {
          weight = elapsed / 80;
        } else if (elapsed < BLINK_DURATION_MS) {
          weight = 1 - (elapsed - 80) / 120;
        } else {
          blinkStartRef.current = null;
          weight = 0;
          if (isDoubleBlinkRef.current && blinkPhaseRef.current === 0) {
            blinkPhaseRef.current = 1;
            setTimeout(() => { blinkStartRef.current = performance.now(); }, 120);
          } else {
            scheduleNext(now);
          }
        }
      } else if (now >= nextBlinkAtRef.current) {
        blinkStartRef.current = now;
      }

      for (const morph of BLINK_MORPHS) {
        const idx = morphTarget.morphTargetDictionary[morph];
        if (idx === undefined) continue;
        morphTarget.morphTargetInfluences[idx] = weight;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      for (const morph of BLINK_MORPHS) {
        const idx = morphTarget.morphTargetDictionary[morph];
        if (idx === undefined) continue;
        morphTarget.morphTargetInfluences[idx] = 0;
      }
      blinkStartRef.current = null;
    };
  }, [morphTarget, enabled, minIntervalMs, maxIntervalMs, doubleBlinkChance]);
}
